"use client";

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const ProductContext = createContext();

export const useFilter = () => {
  const context = useContext(ProductContext);
  if (!context) throw new Error("useFilter must be used within ProductFilterProvider");
  return context;
};

export const ProductFilterProvider = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const [filters, setFilters] = useState({
    brand: [],
    category: "",
    subCategory: "",
    availability: "",
  });

  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0); // filtered or searched count
  const [allProductsCount, setAllProductsCount] = useState(0); // total products from server

  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 8;
  const [loading, setLoading] = useState(false);

  const fetchProductsSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(
        "https://solar-backend-5nh9.onrender.com/public/products/search",
        {
          params: {
            title: query,
            brand: query,
            model: query,
          },
        }
      );

      const fetched = res.data.items || [];

      const unique = fetched.filter(
        (item, index, self) => index === self.findIndex((p) => p._id === item._id)
      );

      setSearchResults(unique);
    } catch (error) {
      console.error("Error fetching search results:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    fetchProductsSearch(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        let response;

        const hasFilters = Object.values(filters).some((value) =>
          Array.isArray(value) ? value.length > 0 : value
        );

       if (searchQuery.trim()) {
  const normalizedSearchResults = searchResults.map((product) => {
    let imageUrl = null;
    let imageAlt = product.title || "Product Image";

    if (product.images?.length > 0) {
      const raw = product.images[0];
      try {
        if (typeof raw === "string") {
          const parsed = JSON.parse(raw);
          imageUrl = parsed.url;
          imageAlt = parsed.alt || product.title;
        } else if (raw.url) {
          imageUrl = raw.url;
          imageAlt = raw.alt || product.title;
        }
      } catch {
        imageUrl = raw;
      }
    }

    return { ...product, imageUrl, imageAlt };
  });

  setProducts(normalizedSearchResults);
  setTotalCount(normalizedSearchResults.length);
  setCurrentPage(1);
  return;
}

        if (hasFilters) {
          const query = new URLSearchParams(
            Object.fromEntries(
              Object.entries(filters).filter(([_, v]) =>
                Array.isArray(v) ? v.length > 0 : v
              )
            )
          ).toString();

          response = await axios.get(
            `https://solar-backend-5nh9.onrender.com/public/products/filter?${query}`
          );
        }

        else {
          response = await axios.get(
            "https://solar-backend-5nh9.onrender.com/public/products"
          );

          setAllProductsCount(response.data.items.length);
        }

        const normalizedProducts = (response.data.items || []).map((product) => {
          let imageUrl = null;
          let imageAlt = product.title || "Product Image";

          if (product.images?.length > 0) {
            const raw = product.images[0];
            try {
              if (typeof raw === "string") {
                const parsed = JSON.parse(raw);
                imageUrl = parsed.url;
                imageAlt = parsed.alt || product.title;
              } else if (raw.url) {
                imageUrl = raw.url;
                imageAlt = raw.alt || product.title;
              }
            } catch {
              imageUrl = raw;
            }
          }

          return { ...product, imageUrl, imageAlt };
        });

        setProducts(normalizedProducts);
        setTotalCount(normalizedProducts.length);
        setCurrentPage(1);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [filters, searchResults, searchQuery]);

  // Product Indexing
  const productsWithIndex = useMemo(() => {
    return products.map((product, idx) => ({
      ...product,
      index: (currentPage - 1) * productsPerPage + idx + 1,
    }));
  }, [products, currentPage]);

  return (
    <ProductContext.Provider
      value={{
        searchQuery,
        setSearchQuery,
        searchResults,
        fetchProductsSearch,
        filters,
        setFilters,
        products,
        setProducts,
        productsWithIndex,
        productsLength: allProductsCount,
        loading,
        totalCount,
        currentPage,
        setCurrentPage,
        productsPerPage,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};
